import { useState, useEffect, useMemo } from 'react'
import { checkSingleValue, tr } from 'mmfintech-commons'
import { globalSettings, TwoFactorTypes, useChallengeRequest } from 'mmfintech-checkout-commons'
import { OtpInput } from './elements/OtpInput'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { OtpResendMessage, PaymentContainer } from '../../CheckoutPayment/Checkout.styled'
import { CoreButton, DurationTimer } from '../../../components'
import type { TwoFactorType } from 'mmfintech-checkout-commons/types'

const getOptTitle = (twoFactorType: TwoFactorType) => {
  switch (twoFactorType) {
    case TwoFactorTypes.SMS:
      return tr('CHECKOUT.OTP.SMS_VERIFICATION', 'SMS verification')

    case TwoFactorTypes.EMAIL:
      return tr('CHECKOUT.OTP.EMAIL_VERIFICATION', 'Email verification')

    case TwoFactorTypes.TOTP:
      return tr('CHECKOUT.OTP.GA_VERIFICATION', 'Google Authenticator verification')

    default:
      return tr('CHECKOUT.OTP.EMAIL_VERIFICATION', 'Email verification')
  }
}

interface WalletCheckoutOtp {
  onSuccess: (response: any) => void
  onCancel?: any
  wallet?: boolean
}

export const WalletCheckoutOtp = ({ onSuccess, onCancel, wallet }: WalletCheckoutOtp) => {
  const [expired, setExpired] = useState(false)
  const [attempts, setAttempts] = useState(0)
  const [verificationCode, setVerificationCode] = useState('')

  const {
    challenge,
    resendCode,
    resendError,
    resendFetching,
    resetChallenge,
    solveChallenge,
    solveError,
    solveFetching
  } = useChallengeRequest(wallet)

  const { challengeId, sentTo, twoFactorType, otpLength } = challenge || {}

  const codeSize = useMemo(() => (otpLength > 0 ? otpLength : globalSettings.otpCodeLength), [otpLength])

  const handleChange = (value: any) => {
    const newValue = checkSingleValue(value, { validation: 'numeric' })
    setVerificationCode(newValue)
  }

  const handleResend = () => {
    setVerificationCode('')
    resendCode(challengeId, () => {
      setAttempts(0)
      // setVerificationCode('')
    })
  }

  useEffect(() => {
    if (verificationCode.length === codeSize && attempts < globalSettings.otpMaxAttempts) {
      setAttempts(attempts + 1)
      solveChallenge(challengeId, verificationCode, onSuccess)
    }
    // eslint-disable-next-line
  }, [verificationCode])

  useEffect(() => {
    return () => {
      resetChallenge()
    }
  }, [])

  return (
    <div className='payment-wrapper'>
      <PaymentContainer className='otp-wrapper vertically-centered' data-test='wallet-checkout-otp'>
        <div className='otp-title'>{getOptTitle(twoFactorType)}</div>
        <div className='otp-subtitle'>
          {twoFactorType === TwoFactorTypes.TOTP
            ? tr('CHECKOUT.OTP.ENTER_CODE', 'Please enter the verification code')
            : tr('CHECKOUT.OTP.ENTER_CODE_TO', "Please enter the verification code that we've sent to")}
          : {twoFactorType !== TwoFactorTypes.TOTP && <span>{sentTo}</span>}
        </div>

        <ErrorDisplay error={solveError} />

        {resendFetching ? null : resendError ? (
          <>
            <ErrorDisplay error={resendError} />

            {onCancel && (
              <CoreButton
                type='button'
                variation='primary'
                fullWidth
                text={tr('CHECKOUT.BUTTONS.CANCEL', 'Cancel')}
                onClick={onCancel}
                data-test='button-cancel'
              />
            )}
          </>
        ) : (
          <>
            <div className='text-center'>
              <OtpInput
                codeLength={codeSize}
                onChange={handleChange}
                disabled={expired || attempts >= globalSettings.otpMaxAttempts}
                value={verificationCode}
              />
            </div>

            {twoFactorType !== TwoFactorTypes.TOTP && (
              <OtpResendMessage>
                {attempts < globalSettings.otpMaxAttempts ? (
                  <>
                    {tr('CHECKOUT.OTP.HAVE_NOT_RECEIVED', "Haven't received it?")}
                    <span onClick={handleResend}>{tr('CHECKOUT.OTP.RESEND_CODE', 'Re-send code')}</span>
                  </>
                ) : (
                  tr('CHECKOUT.OTP.NO_MORE_ATTEMPTS', 'No more verification attempts.')
                )}
              </OtpResendMessage>
            )}

            <div className='mt-3'>
              {onCancel && (
                <CoreButton
                  type='button'
                  variation='primary'
                  text={tr('CHECKOUT.BUTTONS.CANCEL', 'Cancel')}
                  fullWidth
                  disabled={solveFetching}
                  onClick={onCancel}
                  data-test='button-cancel'
                />
              )}
            </div>
          </>
        )}

        {attempts < globalSettings.otpMaxAttempts && !resendFetching && <DurationTimer setCustomExpired={setExpired} />}
      </PaymentContainer>
    </div>
  )
}
